import { create } from 'zustand'

/**
 * Right panel layout store — ancho del `<aside>` del ProjectShell.
 *
 * Permite que el usuario arrastre el divider izquierdo del
 * `RightSidePanel` para hacer el panel más ancho o más angosto, y
 * persiste la preferencia en localStorage entre sesiones del browser.
 *
 * El default (400px) replica el ancho hardcoded original. Min/max
 * (320/720) acotan para que el editor del documento siempre tenga
 * espacio razonable y el panel no se vuelva inservible.
 *
 * Persiste en localStorage bajo `hivanexus.rightPanel.width` (entero
 * en pixels). Si el valor leído no es válido (NaN, fuera de rango)
 * caemos al default — defensivo ante storage tampering.
 *
 * Ver Doc 21 Fase 10 — el resize fue una mejora de QoL pedida tras
 * cerrar la comparación in-card; los reviewers querían más ancho
 * cuando hay diff line-based con líneas largas.
 */

const STORAGE_KEY = 'hivanexus.rightPanel.width'

export const RIGHT_PANEL_MIN_WIDTH = 320
export const RIGHT_PANEL_MAX_WIDTH = 720
export const RIGHT_PANEL_DEFAULT_WIDTH = 400

function clampWidth(px: number): number {
  if (!Number.isFinite(px)) return RIGHT_PANEL_DEFAULT_WIDTH
  return Math.max(
    RIGHT_PANEL_MIN_WIDTH,
    Math.min(RIGHT_PANEL_MAX_WIDTH, Math.round(px)),
  )
}

function readInitialWidth(): number {
  if (typeof localStorage === 'undefined') return RIGHT_PANEL_DEFAULT_WIDTH
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return RIGHT_PANEL_DEFAULT_WIDTH
    const parsed = Number.parseInt(raw, 10)
    if (!Number.isFinite(parsed)) return RIGHT_PANEL_DEFAULT_WIDTH
    return clampWidth(parsed)
  } catch {
    return RIGHT_PANEL_DEFAULT_WIDTH
  }
}

function persistWidth(px: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(px))
  } catch {
    /* localStorage no disponible (private mode, etc.) — silenciar */
  }
}

interface RightPanelLayoutState {
  width: number
  /** Setter durante el drag. Aplica clamp y persiste. */
  setWidth: (px: number) => void
  /** Reset al default (dblclick del divider). */
  resetWidth: () => void
}

export const useRightPanelLayoutStore = create<RightPanelLayoutState>(
  (set) => ({
    width: readInitialWidth(),
    setWidth: (px) => {
      const next = clampWidth(px)
      persistWidth(next)
      set({ width: next })
    },
    resetWidth: () => {
      persistWidth(RIGHT_PANEL_DEFAULT_WIDTH)
      set({ width: RIGHT_PANEL_DEFAULT_WIDTH })
    },
  }),
)
